import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core'
import { animate, AnimationBuilder, style } from '@angular/animations'
import difference from 'lodash-es/difference'
import { Observable } from 'rxjs'

import { linxioAnimations } from '@shared/animations'
import { SETTINGS_FIELDS_REWRITE, SettingsFieldsRewrite } from './fields-rewrite'
import { StateService } from '@shared/services/state.service'
import { ScheduledReportService } from '@app/modules/client/config/scheduled-reports/scheduled-report.service'
import { ExportFileTypes } from '@shared/models/export-file-types.enum'
import { LoadingFileService } from '@shared/services/loading-file.service'

@Component({
  selector: 'lin-table-settings',
  templateUrl: './table-settings.component.html',
  styleUrls: ['./table-settings.component.scss'],
  animations: linxioAnimations,
  standalone: false,
})
export class TableSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('panel', { static: true }) panel: ElementRef
  @ViewChild('overlay', { static: true }) overlay: ElementRef

  @Input() columns: string[]
  @Input() exportMap: { [k: string]: string } = {}
  @Input() columnsMandatory: string[] = []
  @Input() dynamicColumns: string[] = []
  @Input() disableExportCSV = false
  @Input() disableExportPDF = false
  @Input() hidden = false

  @Output() exportFile = new EventEmitter<{ columns: string[]; type: ExportFileTypes }>()

  columnsToggleable: string[]
  columnsDisplayed: string[] = []
  columnsSearchDisplayed: string[] = []
  fileTypes = ExportFileTypes
  isFileLoading$: Observable<{ [param: string]: boolean }>
  private removeClickListener: () => void

  @HostBinding('class.bar-closed') isBarClosed = true
  @HostBinding('style.top') tableSettingsTop = '154px'

  constructor(
    private animationBuilder: AnimationBuilder,
    private renderer: Renderer2,
    @Inject(SETTINGS_FIELDS_REWRITE) @Optional() public fieldsRewrite: SettingsFieldsRewrite,
    private stateService: StateService,
    private cdr: ChangeDetectorRef,
    private scheduledReportService: ScheduledReportService,
    private loadingFileService: LoadingFileService,
  ) {
    this.fieldsRewrite = fieldsRewrite || {}
  }

  ngOnInit() {
    this.removeClickListener = this.renderer.listen(this.overlay.nativeElement, 'click', () => this.closeBar())
    this.isFileLoading$ = this.loadingFileService.isFileLoading$

    if (this.isUpdateColumns()) {
      this.stateService.saveTableColumns(this.columns)
    }
    void Promise.resolve().then(() => this.initDisplayedColumns())

    const resizeObserver = new ResizeObserver((entries) => {
      const bannerHeight = entries[0].target.clientHeight
      this.tableSettingsTop = String(154 + bannerHeight) + 'px'
    })

    setTimeout(() => {
      const banner = document.getElementById('warning-banner')
      banner && resizeObserver.observe(document.getElementById('warning-banner'))
    })
  }

  initDisplayedColumns() {
    const onlyToggleable = (column: string) => !this.columnsMandatory.includes(column)
    this.columnsToggleable = this.columns.filter(onlyToggleable)

    const columns = this.columns.slice()
    const hiddenColumns = this.stateService.getTableHiddenColumns()
    const displayedColumns = difference(columns, hiddenColumns)

    this.setDisplayedColumns(displayedColumns)
  }

  toggleColumn(column: string) {
    if (this.columnsDisplayed.includes(column)) {
      this.turnColumnOff(column)
    } else {
      this.turnColumnOn(column)
    }

    const hiddenColumns = difference(this.columns, this.columnsDisplayed)
    this.stateService.saveTableHiddenColumns(hiddenColumns)
  }

  private isUpdateColumns() {
    return !this.stateService.getTableColumns()?.length || this.columns?.length !== this.stateService.getTableColumns()?.length
  }

  private turnColumnOff(column: string): void {
    const onlyDisplayed = (filteringColumn: string) => filteringColumn !== column
    const displayedColumns = this.columnsDisplayed.filter(onlyDisplayed)

    this.setDisplayedColumns(displayedColumns)
  }

  private turnColumnOn(column: string) {
    const onlyDisplayed = (filteringColumn: string) => {
      const isMandatory = this.columnsMandatory.includes(filteringColumn)
      const isDisplayed = this.columnsDisplayed.includes(filteringColumn)
      const isTurnedOn = filteringColumn === column

      return isDisplayed || isMandatory || isTurnedOn
    }
    const displayedColumns = this.columns.filter(onlyDisplayed)

    this.setDisplayedColumns(displayedColumns)
  }

  private columnsForSchedulingAndExport(): string[] {
    const byMandatory = (field: string) => !this.columnsMandatory.includes(field)
    const rewriteColumnNames = (field: string) => this.exportMap[field] || field
    return this.columnsDisplayed.filter(byMandatory).map(rewriteColumnNames)
  }

  setDisplayedColumns(columns: string[]) {
    const columnsSearchDisplayed = columns.map((column) => `${column}-search`)
    this.columnsDisplayed = columns
    this.columnsSearchDisplayed = columnsSearchDisplayed
    this.scheduledReportService.setFields(this.columnsForSchedulingAndExport())
  }

  isColumnDisplayed(column: string) {
    return this.columnsDisplayed.includes(column)
  }

  closeBar() {
    this.isBarClosed = true
    this.animationBuilder
      .build([style({ transform: 'translate3d(0,0,0)' }), animate('400ms ease', style({ transform: 'translate3d(100%,0,0)' }))])
      .create(this.panel.nativeElement)
      .play()
    this.cdr.detectChanges()
  }

  openBar() {
    this.isBarClosed = false
    this.animationBuilder
      .build([style({ transform: 'translate3d(100%,0,0)' }), animate('400ms ease', style({ transform: 'translate3d(0,0,0)' }))])
      .create(this.panel.nativeElement)
      .play()
  }

  exportFileColumns(type: ExportFileTypes): void {
    const columnsForExport = this.columnsForSchedulingAndExport()

    this.exportFile.emit({ columns: columnsForExport, type })
  }

  ngOnDestroy() {
    this.removeClickListener()
  }
}
